/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '../../theme/theme';
import {LabelTypography} from '../../typography/LabelTypography';

// defines the props definitions...
interface PulseLoaderProps {
  message?: string;
};

// defines the style definitions...
const useStyles = makeStyles(theme => ({
  spinnerContainer : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  spinnerDots : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  spinnerDot : {
    width                   : '4px',
    height                  : '4px',
    borderRadius            : '50%',
    backgroundColor         : dlsColor('grey2'),
    margin                  : '0 2px',
    opacity                 : 0,
    animation               : '$loaderAnimation',
    animationDuration       : '1.5s',
    animationIterationCount : 'infinite',
    '&:nth-child(2)'        : {
      animationDelay : '0.5s'
    },
    '&:nth-child(3)' : {
      animationDelay : '1s'
    }
  },
  '@keyframes loaderAnimation' : {
    '0%, 100%' : {
      opacity : 0
    },
    '50%' : {
      opacity : 1
    }
  }
}));

/**
 * A component that displays a loading spinner with three animated dots.
 *
 * ```typescript
 *
 *  <PulseLoader />
 *  <PulseLoader message={'fetching'} />
 *
 * ```
 *
 * @param message - Loading message. Defaults to 'Loading'.
 */
export const PulseLoader = ({message = translate('common:states.loading')}: PulseLoaderProps) => {
  const classes = useStyles();
  return (
    <div className={classes.spinnerContainer}>
      <LabelTypography>
        {message.includes('...') ? message.substring(0, message.length - 3) : message}
      </LabelTypography>
      <div className={classes.spinnerDots}>
        <div className={classes.spinnerDot} />
        <div className={classes.spinnerDot} />
        <div className={classes.spinnerDot} />
      </div>
    </div>
  );
};

export default PulseLoader;
